import { msg } from "@lingui/core/macro";
import { createEffect, createMemo, createSignal, For, type JSX, Show, untrack } from "solid-js";
import { useNavigate, useSearch } from "@tanstack/solid-router";
import { Check, Loader2, Plus, Search, Trash2, Type } from "@fleet/icons";
import { Button } from "@/components/ui/button";
import { Card, CardContent, Skeleton, Switch, TextField } from "@fleet/ui";
import { Tabs, TabsList, TabsPanel, TabsPanels, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { api, createApiMutation, createApiQuery } from "@/lib/api";
import { queryClient } from "@/lib/query-client";
import {
  type AdminFontRow,
  type components,
  type FontDownloaderVariant,
  getAdminFontStats,
  isFontWeight,
  queryKeys,
} from "@journal/shared";
import { showToast } from "@/components/ui/toast";
import { useI18n } from "@/providers/i18n-provider";
import { FontDownloaderService } from "@/utils/font-downloader";
import { QueryErrorState } from "@/components/ui/query-error-state";

type ErrorModel = components["schemas"]["ErrorModel"];
type RegistryResponse = components["schemas"]["FontRegistryResponse"];
type RegistryFont = components["schemas"]["RegistryFontDTO"];

const PREVIEW_WEIGHTS = [400, 700] as const;
/* oxlint-disable lingui/no-unlocalized-strings -- font preview specimens (glyph samples, not UI copy) */
// A pangram exercises the whole alphabet so the typeface is easy to judge.
const PANGRAM = "The quick brown fox jumps over the lazy dog";
const SAMPLE_ALPHA = "ABCDEFGHIJKLMNOPQRSTUVWXYZ abcdefghijklmnopqrstuvwxyz";
const SAMPLE_NUM = "0123456789 — !?&@#%";
/* oxlint-enable lingui/no-unlocalized-strings */

/** Stable CORS-enabled woff2 URL on the Fontsource CDN (same pattern as backend). */
function fontsourceWoff2Url(id: string, weight: number): string {
  return `https://cdn.jsdelivr.net/npm/@fontsource/${id}@5/files/${id}-latin-${weight}-normal.woff2`;
}

function toDownloaderVariants(raw: { weight: string; url: string }[]): FontDownloaderVariant[] {
  return raw.flatMap((v) => (isFontWeight(v.weight) ? [{ weight: v.weight, url: v.url }] : []));
}

/**
 * Admin font management, two URL-addressable tabs (`?tab=available|approved`):
 *  - "Available": the third-party registry (Fontsource). Browse the full catalog
 *    and APPROVE a font → ingested with CDN URLs, set active, synced to clients.
 *  - "Approved": the local catalog with publish/unpublish + remove.
 * Any font is clickable → opens a live preview at its own URL (`?font=<id>`).
 */
export function FontManager() {
  const { t } = useI18n();
  const search = useSearch({ from: "/_app/admin/fonts" });
  const navigate = useNavigate();

  const tab = (): "available" | "approved" => search().tab ?? "available";
  const setTab = (v: string) => {
    void navigate({
      to: "/admin/fonts",
      search: { tab: v === "approved" ? "approved" : "available" },
      replace: true,
    });
  };
  const previewId = () => search().font;
  const setPreview = (id: string | undefined) => {
    void navigate({ to: "/admin/fonts", search: { tab: tab(), font: id }, replace: true });
  };

  return (
    <div class="space-y-8">
      <div class="flex items-center gap-3">
        <Type class="h-8 w-8 text-primary" />
        <div>
          <h2 class="text-3xl font-bold tracking-tight">{t(msg`Font Catalog`)}</h2>
          <p class="text-sm text-muted-foreground">
            {t(msg`Browse the font library and approve the ones your users can pick.`)}
          </p>
        </div>
      </div>

      <Tabs value={tab()} onChange={setTab}>
        <TabsList>
          <TabsTrigger value="available">{t(msg`Available`)}</TabsTrigger>
          <TabsTrigger value="approved">{t(msg`Approved`)}</TabsTrigger>
        </TabsList>
        {/* Render ONLY the active panel: `TabsPanel` (Kobalte Content) uses
            `forceMount`, which does NOT hide the inactive panel — without this
            both panels would render stacked at once (the reported "clicking the
            tabs does nothing"). The active tab keys the animated transition. */}
        <TabsPanels value={tab()} order={["available", "approved"]}>
          <Show when={tab() === "available"}>
            <TabsPanel value="available">
              <AvailableTab active={() => true} previewId={previewId} onPreview={setPreview} />
            </TabsPanel>
          </Show>
          <Show when={tab() === "approved"}>
            <TabsPanel value="approved">
              <ApprovedTab active={() => true} previewId={previewId} onPreview={setPreview} />
            </TabsPanel>
          </Show>
        </TabsPanels>
      </Tabs>
    </div>
  );
}

// ─── Preview dialog (URL-addressable per font) ─────────────────────────────────

interface FontPreviewDialogProps {
  open: boolean;
  onClose: () => void;
  family: string;
  subtitle: string;
  variants: FontDownloaderVariant[];
  footer?: JSX.Element;
}

function FontPreviewDialog(props: FontPreviewDialogProps) {
  const { t } = useI18n();
  const [loading, setLoading] = createSignal(false);
  const [rev, setRev] = createSignal(0);

  // Load the actual typeface when the dialog opens so the sample renders in it
  // (cache-first; works offline once seen). `rev` bumps to re-style after load.
  createEffect(() => {
    if (!props.open || props.variants.length === 0) {
      return;
    }
    setLoading(true);
    void FontDownloaderService.downloadAndLoad(props.family, props.variants).finally(() => {
      setLoading(false);
      setRev((n) => n + 1);
    });
  });

  const fontStack = () => {
    void rev();
    return `'${props.family}', ui-sans-serif, system-ui, sans-serif`;
  };

  return (
    <Dialog open={props.open} onOpenChange={(o) => !o && props.onClose()} contentClass="max-w-2xl">
      <DialogHeader>
        <DialogTitle>{props.family}</DialogTitle>
        <p class="text-sm text-muted-foreground">{props.subtitle}</p>
      </DialogHeader>

      <div class="space-y-6 p-4">
        <Show when={loading()}>
          <p class="text-xs text-muted-foreground inline-flex items-center gap-2">
            <Loader2 class="h-3 w-3 animate-spin" /> {t(msg`Loading preview...`)}
          </p>
        </Show>

        <div style={{ "font-family": fontStack() }} class="space-y-4">
          <p class="text-3xl leading-tight">{PANGRAM}</p>
          <p class="text-xl font-bold leading-tight">{PANGRAM}</p>
          <p class="text-base leading-relaxed">{SAMPLE_ALPHA}</p>
          <p class="text-base leading-relaxed">{SAMPLE_NUM}</p>
        </div>
      </div>

      <Show when={props.footer}>
        <div class="border-t border-border/40 p-4">{props.footer}</div>
      </Show>
    </Dialog>
  );
}

// ─── Available (third-party registry) ──────────────────────────────────────────

interface TabProps {
  /** Whether this tab is the active one (only the active tab renders its preview). */
  active: () => boolean;
  previewId: () => string | undefined;
  onPreview: (id: string | undefined) => void;
}

function AvailableTab(props: TabProps) {
  const { t } = useI18n();
  const urlSearch = useSearch({ from: "/_app/admin/fonts" });
  const navigate = useNavigate();
  const [search, setSearch] = createSignal(urlSearch().q ?? "");
  const [category, setCategory] = createSignal<string>(urlSearch().category ?? "all");
  const [approvingId, setApprovingId] = createSignal<string | null>(null);

  const CATEGORIES = ["all", "sans-serif", "serif", "display", "handwriting", "monospace"] as const;

  // Filters are URL-addressable (`?q=` + `?category=`): shareable/deep-linkable
  // and they survive a reload. `replace` avoids history spam while typing.
  const syncUrl = (q: string, cat: string) => {
    void navigate({
      to: "/admin/fonts",
      search: {
        tab: "available",
        ...(q.trim() ? { q: q.trim() } : {}),
        ...(cat === "all" ? {} : { category: cat }),
      },
      replace: true,
    });
  };

  // Reflect external URL changes (back/forward, pasted link) into the signals.
  createEffect(() => {
    const q = urlSearch().q ?? "";
    const cat = urlSearch().category ?? "all";
    if (q !== untrack(search)) {
      setSearch(q);
    }
    if (cat !== untrack(category)) {
      setCategory(cat);
    }
  });

  const registryQuery = createApiQuery<RegistryResponse, ErrorModel>(
    () => queryKeys.adminFonts.registry(search().trim().toLowerCase(), category()),
    async () =>
      api.GET("/api/admin/font/registry", {
        params: {
          query: {
            search: search().trim() || undefined,
            category: category() === "all" ? undefined : category(),
            pageSize: 60,
          },
        },
      }),
    () => ({ staleTime: 60_000 }),
  );

  const approveMutation = createApiMutation(async (vars: { id: string }) =>
    api.POST("/api/admin/font/approve", { body: vars }),
  );

  const handleApprove = async (font: RegistryFont) => {
    setApprovingId(font.id);
    try {
      await approveMutation.mutateAsync({ id: font.id });
      // Refresh BOTH lists: the registry's `approved` flag and the Approved tab.
      await queryClient.invalidateQueries({ queryKey: queryKeys.adminFonts.all });
      const { family } = font;
      showToast(t(msg`"${family}" approved — now available to users`), "success");
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : String(err);
      showToast(t(msg`Error: ${message}`), "error");
    } finally {
      setApprovingId(null);
    }
  };

  const items = createMemo(() => registryQuery.data?.items ?? []);
  const previewFont = createMemo(() => items().find((f) => f.id === props.previewId()));
  // The preview is open only for THIS tab. We RETAIN the last font so the dialog
  // keeps its content while it plays the exit animation (the URL clears `?font`
  // → `previewFont()` goes null, but `shown()` holds the data until unmount).
  const previewOpen = createMemo(() => props.active() && previewFont() !== undefined);
  const [shown, setShown] = createSignal<RegistryFont>();
  createEffect(() => {
    const f = previewFont();
    if (props.active() && f) {
      setShown(f);
    }
  });

  const previewVariants = createMemo(() => {
    const f = shown();
    if (!f) {
      return [];
    }
    const weights = PREVIEW_WEIGHTS.filter((w) => f.weights.includes(w));
    const chosen = weights.length > 0 ? weights : [f.weights[0] ?? 400];
    return toDownloaderVariants(
      chosen.map((w) => ({ weight: String(w), url: fontsourceWoff2Url(f.id, w) })),
    );
  });

  return (
    <div class="space-y-6 pt-6">
      <div class="relative">
        <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <TextField
          placeholder={t(msg`Search the font library...`)}
          class="pl-9"
          value={search()}
          onInput={(e) => {
            setSearch(e.currentTarget.value);
            syncUrl(e.currentTarget.value, category());
          }}
        />
      </div>

      <div class="flex flex-wrap gap-2">
        <For each={CATEGORIES}>
          {(cat) => (
            <Button
              variant={category() === cat ? "default" : "outline"}
              size="sm"
              class="rounded-full"
              onClick={() => {
                setCategory(cat);
                syncUrl(search(), cat);
              }}
            >
              {cat === "all" ? t(msg`All`) : cat}
            </Button>
          )}
        </For>
      </div>

      {/* P23/CC-60 · I3: «no hay respuesta» ≠ «no hay nada». En error
          `isLoading`/`isPending` valen FALSE y `.data` no tiene valor, así que
          el guard dejaba pasar y la pantalla afirmaba que no hay nada. */}
      <Show when={registryQuery.isError}>
        <QueryErrorState
          testId="font-registry-error"
          compact
          onRetry={() => void registryQuery.refetch()}
        />
      </Show>
      <Show when={!registryQuery.isError}>
        <Show
          when={!registryQuery.isLoading}
          fallback={
            <div class="grid gap-3 md:grid-cols-2">
              <For each={Array.from({ length: 6 }, () => 0)}>
                {() => <Skeleton class="h-20 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <Show
            when={items().length > 0}
            fallback={
              <p class="text-sm text-muted-foreground py-8 text-center">
                {t(msg`No fonts match your search.`)}
              </p>
            }
          >
            <div class="grid gap-3 md:grid-cols-2">
              <For each={items()}>
                {(font) => (
                  <Card
                    class="cursor-pointer transition-colors hover:bg-accent/5"
                    onClick={() => props.onPreview(font.id)}
                  >
                    <CardContent class="flex items-center justify-between gap-4 p-4">
                      <div class="min-w-0">
                        <div class="font-bold truncate">{font.family}</div>
                        <div class="text-xs text-muted-foreground">
                          {font.category} • {font.weights.length} {t(msg`weights`)}
                        </div>
                      </div>
                      <Show
                        when={!font.approved}
                        fallback={
                          <span class="inline-flex items-center gap-1 text-sm font-medium text-success shrink-0">
                            <Check class="h-4 w-4" /> {t(msg`Approved`)}
                          </span>
                        }
                      >
                        <Button
                          size="sm"
                          class="shrink-0"
                          disabled={approvingId() === font.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            void handleApprove(font);
                          }}
                        >
                          <Show
                            when={approvingId() === font.id}
                            fallback={
                              <>
                                <Plus class="mr-2 h-4 w-4" /> {t(msg`Approve`)}
                              </>
                            }
                          >
                            <Loader2 class="mr-2 h-4 w-4 animate-spin" /> {t(msg`Approving...`)}
                          </Show>
                        </Button>
                      </Show>
                    </CardContent>
                  </Card>
                )}
              </For>
            </div>
          </Show>
        </Show>
      </Show>

      <FontPreviewDialog
        open={previewOpen()}
        onClose={() => props.onPreview(undefined)}
        family={shown()?.family ?? ""}
        subtitle={(() => {
          const f = shown();
          return f ? `${f.category} • ${f.weights.length} ${t(msg`weights`)}` : "";
        })()}
        variants={previewVariants()}
        footer={
          <Show when={shown()}>
            {(f) => (
              <Show
                when={!f().approved}
                fallback={
                  <span class="inline-flex items-center gap-1 text-sm font-medium text-success">
                    <Check class="h-4 w-4" /> {t(msg`Already approved`)}
                  </span>
                }
              >
                <Button
                  disabled={approvingId() === f().id}
                  onClick={() => {
                    void handleApprove(f());
                  }}
                >
                  <Plus class="mr-2 h-4 w-4" /> {t(msg`Approve this font`)}
                </Button>
              </Show>
            )}
          </Show>
        }
      />
    </div>
  );
}

// ─── Approved (local catalog) ──────────────────────────────────────────────────

function ApprovedTab(props: TabProps) {
  const { t } = useI18n();
  const [search, setSearch] = createSignal("");
  const [pendingDelete, setPendingDelete] = createSignal<AdminFontRow | null>(null);
  const [pendingUnpublish, setPendingUnpublish] = createSignal<AdminFontRow | null>(null);

  const fontsQuery = createApiQuery<AdminFontRow[], ErrorModel>(
    () => queryKeys.adminFonts.all,
    async () => api.GET("/api/admin/font/listAll", {}),
    () => ({ staleTime: 30_000 }),
  );

  const toggleMutation = createApiMutation(async (vars: { fontId: string; isActive: boolean }) =>
    api.POST("/api/admin/font/toggleActive", { body: vars }),
  );

  const deleteMutation = createApiMutation(async (vars: { fontId: string }) =>
    api.POST("/api/admin/font/delete", { body: vars }),
  );

  const filteredFonts = createMemo(() => {
    const s = search().toLowerCase();
    const data = fontsQuery.data ?? [];
    return data.filter(
      (f) => f.family.toLowerCase().includes(s) || f.name.toLowerCase().includes(s),
    );
  });

  const stats = createMemo(() => getAdminFontStats(fontsQuery.data ?? []));
  const previewFont = createMemo(() =>
    (fontsQuery.data ?? []).find((f) => f.id === props.previewId()),
  );
  // Retain the last previewed font so the dialog keeps its content during the
  // exit animation (see AvailableTab for the rationale).
  const previewOpen = createMemo(() => props.active() && previewFont() !== undefined);
  const [shown, setShown] = createSignal<AdminFontRow>();
  createEffect(() => {
    const f = previewFont();
    if (props.active() && f) {
      setShown(f);
    }
  });

  const previewVariants = createMemo(() => {
    const f = shown();
    if (!f) {
      return [];
    }
    return toDownloaderVariants(f.variants.map((v) => ({ weight: v.weight, url: v.s3Key })));
  });

  const applyActive = (font: AdminFontRow, isActive: boolean) => {
    toggleMutation.mutate(
      { fontId: font.id, isActive },
      // Invalidate BOTH lists (approved + registry approved-flag).
      {
        onSuccess: () => void queryClient.invalidateQueries({ queryKey: queryKeys.adminFonts.all }),
      },
    );
  };

  // Publishing is safe → apply directly. Unpublishing affects users currently
  // using the font, so we explain the impact first (with the usage count). When
  // triggered from the preview dialog we close it first so the confirm stands
  // alone (no stacked modals).
  const setActive = (font: AdminFontRow, isActive: boolean) => {
    if (!isActive) {
      props.onPreview(undefined);
      setPendingUnpublish(font);
      return;
    }
    applyActive(font, true);
  };

  const confirmUnpublish = () => {
    const font = pendingUnpublish();
    if (!font) {
      return;
    }
    applyActive(font, false);
    setPendingUnpublish(null);
  };

  const confirmDelete = () => {
    const font = pendingDelete();
    if (!font) {
      return;
    }
    deleteMutation.mutate(
      { fontId: font.id },
      {
        onSuccess: () => {
          void queryClient.invalidateQueries({ queryKey: queryKeys.adminFonts.all });
          props.onPreview(undefined);
          const { family } = font;
          showToast(t(msg`Font "${family}" deleted`), "success");
        },
      },
    );
    setPendingDelete(null);
  };

  return (
    <div class="space-y-6 pt-6">
      <div class="flex items-center justify-between gap-4">
        <p class="text-sm text-muted-foreground">
          {(() => {
            const { total } = stats();
            const { active } = stats();
            return t(msg`${total} approved • ${active} published`);
          })()}
        </p>
        <div class="relative w-full max-w-xs">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <TextField
            placeholder={t(msg`Filter approved...`)}
            class="pl-9"
            value={search()}
            onInput={(e) => setSearch(e.currentTarget.value)}
          />
        </div>
      </div>

      {/* P23/CC-60 · I3: «no hay respuesta» ≠ «no hay nada». En error
          `isLoading`/`isPending` valen FALSE y `.data` no tiene valor, así que
          el guard dejaba pasar y la pantalla afirmaba que no hay nada. */}
      <Show when={fontsQuery.isError}>
        <QueryErrorState
          testId="admin-fonts-error"
          compact
          onRetry={() => void fontsQuery.refetch()}
        />
      </Show>
      <Show when={!fontsQuery.isError}>
        <Show
          when={!fontsQuery.isLoading}
          fallback={
            <div class="grid gap-4 md:grid-cols-2">
              <For each={Array.from({ length: 4 }, () => 0)}>
                {() => <Skeleton class="h-24 w-full rounded-xl" />}
              </For>
            </div>
          }
        >
          <div class="grid gap-4 md:grid-cols-2">
            <For each={filteredFonts()}>
              {(font) => (
                <Card
                  class="cursor-pointer transition-colors hover:bg-accent/5"
                  onClick={() => props.onPreview(font.id)}
                >
                  <CardContent class="flex items-center justify-between gap-4 p-4">
                    <div class="min-w-0">
                      <div class="font-bold truncate">{font.family}</div>
                      <div class="text-xs text-muted-foreground">
                        {font.category} • {font.variants.length} {t(msg`variants`)}
                        {" • "}
                        <Show when={font.isActive} fallback={t(msg`Unpublished`)}>
                          {t(msg`Published`)}
                        </Show>
                        {" • "}
                        {(() => {
                          const count = font.usageCount;
                          return t(msg`${count} in use`);
                        })()}
                      </div>
                    </div>
                    <div
                      class="flex items-center gap-2 shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <Switch
                        checked={font.isActive}
                        onCheckedChange={(v) => setActive(font, v)}
                        aria-label={t(msg`Published`)}
                      />
                    </div>
                  </CardContent>
                </Card>
              )}
            </For>
          </div>
        </Show>
      </Show>

      <FontPreviewDialog
        open={previewOpen()}
        onClose={() => props.onPreview(undefined)}
        family={shown()?.family ?? ""}
        subtitle={(() => {
          const f = shown();
          return f ? `${f.category} • ${f.variants.length} ${t(msg`variants`)}` : "";
        })()}
        variants={previewVariants()}
        footer={
          <Show when={shown()}>
            {(f) => (
              <div class="space-y-3">
                <p class="text-xs text-muted-foreground">
                  {(() => {
                    const count = f().usageCount;
                    return t(msg`${count} in use`);
                  })()}
                </p>
                <div class="flex items-center justify-between gap-4">
                  <label class="flex items-center gap-2 text-sm font-medium">
                    <Switch checked={f().isActive} onCheckedChange={(v) => setActive(f(), v)} />
                    {t(msg`Published`)}
                  </label>
                  <Button
                    variant="ghost"
                    class="text-destructive hover:text-destructive"
                    onClick={() => {
                      const font = f();
                      props.onPreview(undefined);
                      setPendingDelete(font);
                    }}
                  >
                    <Trash2 class="mr-2 h-4 w-4" /> {t(msg`Delete`)}
                  </Button>
                </div>
              </div>
            )}
          </Show>
        }
      />

      {/* Unpublishing is reversible: the font stops syncing → everyone (incl. the
          users currently using it) falls back to the default until it is published
          again; their choice is preserved. Deleting is permanent and resets the
          choice of every user who had it selected. */}
      <ConfirmDialog
        open={pendingUnpublish() !== null}
        onOpenChange={(o) => !o && setPendingUnpublish(null)}
        title={t(msg`Unpublish font?`)}
        description={(() => {
          const count = pendingUnpublish()?.usageCount ?? 0;
          return t(
            msg`The ${count} user(s) currently using this font will fall back to the default system font, and new users won't be able to select it — until you publish it again. Nothing is deleted.`,
          );
        })()}
        confirmLabel={t(msg`Unpublish`)}
        onConfirm={confirmUnpublish}
      />

      <ConfirmDialog
        open={pendingDelete() !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title={t(msg`Delete font?`)}
        description={(() => {
          const count = pendingDelete()?.usageCount ?? 0;
          return t(
            msg`This permanently removes the font. The ${count} user(s) who selected it will be reset to the default system font. To hide it temporarily instead, just unpublish it. This cannot be undone.`,
          );
        })()}
        confirmLabel={t(msg`Delete`)}
        variant="destructive"
        onConfirm={confirmDelete}
      />
    </div>
  );
}
